import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Grid,
  HStack,
  Wrap,
  WrapItem,
  Button,
  Icon,
  Badge,
} from "@chakra-ui/react";
import {
  FaArrowRight,
  FaChartLine,
  FaSolarPanel,
  FaHome,
  FaDollarSign,
  FaCheckCircle,
  FaQuoteLeft,
  FaMapMarkerAlt,
  FaCalendarAlt,
} from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { useNavigate } from "react-router-dom";

const PROPERTY_PRIMARY = "#0D7F6E";
const NAVY = "#141C49";
const NAVY_MID = "#1C2A5E";

type Category =
  | "All Case Studies"
  | "Portfolio Building"
  | "Solar Value-Add"
  | "Equity Strategy"
  | "SMSF / Super";

const CATEGORIES: Category[] = [
  "All Case Studies",
  "Portfolio Building",
  "Solar Value-Add",
  "Equity Strategy",
  "SMSF / Super",
];

interface Metric {
  value: string;
  label: string;
  accent?: boolean;
}

interface CaseStudy {
  id: string;
  category: Exclude<Category, "All Case Studies">;
  title: string;
  subtitle: string;
  location: string;
  duration: string;
  intro: string;
  challenge: string;
  solution: string;
  results: string[];
  metrics: Metric[];
  ctaLabel: string;
  icon: React.ElementType;
}

const CASE_STUDIES: CaseStudy[] = [
  {
    id: "dual-income-professionals",
    category: "Portfolio Building",
    title: "Dual-Income Professionals Building Retirement Wealth",
    subtitle: "From Paying High Tax to Strategic Wealth Building",
    location: "Brisbane, QLD",
    duration: "18 months",
    intro:
      "A professional couple earning $280,000 combined were concerned their super wouldn't deliver their desired retirement. They wanted to reduce their tax bill while building wealth outside super.",
    challenge:
      "Paying over $90,000 annually in tax with no strategy to reduce it. Super projections showed a gap of $400,000 between their balance and desired retirement income.",
    solution:
      "A staged acquisition strategy focused on tax efficiency. The first property reduced their taxable income significantly, then refinancing funded a second purchase with integrated solar to maximise deductions and yields.",
    results: [
      "Built a 2-property portfolio valued at $1.2M",
      "Reduced annual tax by $30,000 through strategic structuring",
      "Refinancing added $65,000 in increased property value",
      "Now on track to close their retirement gap",
    ],
    metrics: [
      { value: "$1.2M", label: "Portfolio Value" },
      { value: "7.2%", label: "Gross Yield", accent: true },
      { value: "$65K", label: "Value Added" },
    ],
    ctaLabel: "Get Similar Results",
    icon: FaChartLine,
  },
  {
    id: "solar-value-add",
    category: "Solar Value-Add",
    title: "Solar Value-Add Success Story",
    subtitle: "Maximising Returns Through Green Investment",
    location: "Gold Coast, QLD",
    duration: "8 months",
    intro:
      "An existing landlord transformed an underperforming rental property into a high-demand, premium-yielding asset through strategic solar installation.",
    challenge:
      "The property had been vacant for 3 months with no tenant interest. Energy costs were a major concern for prospective renters in the area.",
    solution:
      "Installed a 10kW solar system with battery storage, rebranded the listing as an 'eco-friendly home', and repositioned the property in the premium rental market.",
    results: [
      "Leased within 2 weeks of solar installation",
      "Achieved 22% rent increase ($640 to $820/week)",
      "Property value increased by $45,000",
      "Tenant signed a 2-year lease for stability",
    ],
    metrics: [
      { value: "22%", label: "Rent Increase", accent: true },
      { value: "$45K", label: "Value Gained" },
      { value: "2 wks", label: "Time to Lease" },
    ],
    ctaLabel: "Boost My Property",
    icon: FaSolarPanel,
  },
  {
    id: "first-home-to-investment",
    category: "Equity Strategy",
    title: "First Home to Investment Property",
    subtitle: "Strategic Equity Release for Wealth Building",
    location: "Sydney, NSW",
    duration: "24 months",
    intro:
      "A young couple converted their first home into an investment property while purchasing their dream family home, creating a wealth-building asset base without selling.",
    challenge:
      "They wanted to upgrade to a larger home but didn't want to sell their existing property. They needed to access equity while maintaining serviceability.",
    solution:
      "Structured a refinance to release $280,000 in equity, converted the existing home to an investment property, and arranged financing for a new family home with integrated solar.",
    results: [
      "Retained original property as investment ($790K value)",
      "Purchased new family home for $1.2M",
      "Investment property generating $650/week rental income",
      "Tax benefits from negative gearing in early years",
    ],
    metrics: [
      { value: "$1.85M", label: "Total Invested" },
      { value: "$650", label: "Weekly Rent", accent: true },
      { value: "$280K", label: "Equity Released" },
    ],
    ctaLabel: "Unlock My Equity",
    icon: FaHome,
  },
  {
    id: "closing-super-gap",
    category: "SMSF / Super",
    title: "Closing the $500K Super Gap",
    subtitle: "When Super Projections Fall Short of Retirement Dreams",
    location: "Melbourne, VIC",
    duration: "12 months",
    intro:
      "A couple in their early 50s with $320,000 in super realised it wouldn't deliver their desired retirement lifestyle. They needed a strategy to bridge the gap without relying solely on volatile share markets.",
    challenge:
      "Super projections showed they'd have $650,000 at retirement — but they needed $1.3M to maintain their lifestyle. Market volatility in recent years had eroded confidence in their existing super strategy.",
    solution:
      "Identified a commercial property suitable for SMSF purchase, structured it with compliant borrowing, and added solar to maximise returns flowing back into their fund.",
    results: [
      "Acquired $890,000 commercial property through SMSF",
      "Two tenants now lease with 3% annual increases",
      "Net yield of 6.8% flowing into super fund",
      "Now projected to exceed their $1.3M retirement target",
    ],
    metrics: [
      { value: "$890K", label: "Property Value" },
      { value: "6.8%", label: "Net Yield", accent: true },
      { value: "$400K", label: "Super Gap Closed" },
    ],
    ctaLabel: "Close My Super Gap",
    icon: FaDollarSign,
  },
];

const STATS = [
  { value: "$45M", suffix: "+", label: "Client Portfolio Value" },
  { value: "18.5", suffix: "%", label: "Average Annual Return" },
  { value: "150", suffix: "+", label: "Properties Advised" },
  { value: "95", suffix: "%", label: "Client Satisfaction" },
];

const TESTIMONIAL = {
  quote:
    "We were paying a fortune in tax and worried our super wouldn't be enough. One Property gave us a clear plan — now we're saving $15,000 a year in tax and building real wealth for retirement. For the first time, we feel confident about our financial future.",
  name: "Michael & Sarah T.",
  description: "Combined income $280k · Two property portfolio",
  stats: [
    { value: "$15K", label: "Saved in tax annually" },
    { value: "$1.2M", label: "Portfolio built in 18 months" },
  ],
};

const PropertyCaseStudies: React.FC = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState<Category>("All Case Studies");

  const filtered =
    activeFilter === "All Case Studies"
      ? CASE_STUDIES
      : CASE_STUDIES.filter((study) => study.category === activeFilter);

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/property/case-studies"
        fallbackTitle="Property Investment Case Studies"
        fallbackDescription="Real success stories from One Property investors. See how our clients have built wealth through strategic property investment and solar value-add."
      />
      <NavbarWithCallToAction />

      {/* Hero Section - solid navy */}
      <Box bg={NAVY} color="white" py={{ base: 16, md: 24 }}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Badge
              bg="transparent"
              color={PROPERTY_PRIMARY}
              border="1px solid"
              borderColor="rgba(13, 127, 110, 0.4)"
              px={4}
              py={1.5}
              borderRadius="full"
              fontSize="xs"
              fontWeight="600"
              textTransform="uppercase"
              letterSpacing="0.15em"
            >
              Client Case Studies
            </Badge>
            <Heading size="3xl" fontWeight="black" maxW="4xl" lineHeight="1.15">
              Real results for{" "}
              <Text as="span" color={PROPERTY_PRIMARY} fontStyle="italic">
                high-income earners
              </Text>{" "}
              building retirement wealth
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl" lineHeight="1.8">
              See how Australians earning well — but worried about tax and
              super — have used property to build lasting financial security.
            </Text>
            <HStack spacing={4} pt={4}>
              <Button
                size="lg"
                bg={PROPERTY_PRIMARY}
                color="white"
                _hover={{ bg: "#0a6459" }}
                rightIcon={<FaArrowRight />}
                px={8}
                transition="all 0.2s"
                onClick={() => navigate("/property/consultation")}
              >
                Book a Consultation
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderColor="white"
                color="white"
                _hover={{ bg: "whiteAlpha.200" }}
                px={8}
                transition="all 0.2s"
                onClick={() => navigate("/property")}
              >
                Our Approach
              </Button>
            </HStack>
          </VStack>
        </Container>
      </Box>

      {/* Stats Bar - flush beneath hero, navy-mid for subtle separation */}
      <Box bg={NAVY_MID} borderTop="1px solid" borderColor="whiteAlpha.200">
        <Container maxW="container.xl">
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={0} py={{ base: 6, md: 0 }}>
            {STATS.map((stat, index) => (
              <VStack
                key={index}
                py={{ base: 4, md: 7 }}
                spacing={1}
                borderRight={{
                  md: index < STATS.length - 1 ? "1px solid" : "none",
                }}
                borderColor="whiteAlpha.200"
              >
                <Heading size="lg" color="white" fontWeight="bold">
                  {stat.value}
                  <Text as="span" color="whiteAlpha.700">
                    {stat.suffix}
                  </Text>
                </Heading>
                <Text
                  fontSize="xs"
                  color="whiteAlpha.700"
                  textTransform="uppercase"
                  letterSpacing="0.05em"
                  textAlign="center"
                >
                  {stat.label}
                </Text>
              </VStack>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Filter Tabs */}
      <Container maxW="container.xl" pt={{ base: 10, md: 14 }}>
        <Wrap spacing={3} justify="center">
          {CATEGORIES.map((category) => {
            const isActive = category === activeFilter;
            return (
              <WrapItem key={category}>
                <Button
                  size="sm"
                  borderRadius="full"
                  aria-pressed={isActive}
                  bg={isActive ? NAVY : "white"}
                  color={isActive ? "white" : "gray.600"}
                  border="1px solid"
                  borderColor={isActive ? NAVY : "gray.200"}
                  _hover={{ bg: isActive ? NAVY : "gray.50" }}
                  onClick={() => setActiveFilter(category)}
                >
                  {category}
                </Button>
              </WrapItem>
            );
          })}
        </Wrap>
      </Container>

      {/* Case Studies Section */}
      <Container maxW="container.xl" py={{ base: 10, md: 16 }}>
        <VStack spacing={10}>
          {filtered.map((study) => (
            <Box
              key={study.id}
              w="full"
              bg="white"
              borderRadius="2xl"
              boxShadow="lg"
              overflow="hidden"
              border="1px solid"
              borderColor="gray.100"
              transition="all 0.3s"
              _hover={{ boxShadow: "2xl", transform: "translateY(-4px)" }}
            >
              {/* Case Study Header */}
              <Box bg={NAVY} color="white" p={6}>
                <HStack justify="space-between" align="start" flexWrap="wrap" gap={4}>
                  <VStack align="start" spacing={2}>
                    <HStack>
                      <Icon as={study.icon} boxSize={5} color={PROPERTY_PRIMARY} />
                      <Badge
                        bg="transparent"
                        color={PROPERTY_PRIMARY}
                        border="1px solid"
                        borderColor="rgba(13, 127, 110, 0.4)"
                        px={3}
                        py={1}
                        borderRadius="sm"
                        fontSize="xs"
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                      >
                        {study.category}
                      </Badge>
                    </HStack>
                    <Heading size="lg">{study.title}</Heading>
                    <Text color="whiteAlpha.700" fontStyle="italic" fontSize="sm">
                      {study.subtitle}
                    </Text>
                  </VStack>
                  <VStack align="end" spacing={2}>
                    <HStack color="whiteAlpha.700" fontSize="sm">
                      <Icon as={FaMapMarkerAlt} />
                      <Text>{study.location}</Text>
                    </HStack>
                    <HStack
                      color={PROPERTY_PRIMARY}
                      bg="rgba(13, 127, 110, 0.15)"
                      px={3}
                      py={1}
                      borderRadius="sm"
                      fontSize="xs"
                      fontWeight="600"
                    >
                      <Icon as={FaCalendarAlt} />
                      <Text>{study.duration}</Text>
                    </HStack>
                  </VStack>
                </HStack>
              </Box>

              {/* Case Study Content */}
              <Grid templateColumns={{ base: "1fr", lg: "1fr 260px" }}>
                {/* Main Content */}
                <Box p={8}>
                  <VStack align="stretch" spacing={6}>
                    <Box pb={5} borderBottom="1px solid" borderColor="gray.100">
                      <Text color="gray.600" fontSize="md" lineHeight="1.8">
                        {study.intro}
                      </Text>
                    </Box>

                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                      {/* Challenge */}
                      <Box
                        bg="gray.50"
                        p={5}
                        borderRadius="lg"
                        borderLeft="3px solid"
                        borderColor={NAVY}
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          letterSpacing="0.08em"
                          textTransform="uppercase"
                          color={NAVY}
                          mb={2}
                        >
                          The Challenge
                        </Text>
                        <Text color="gray.700" fontSize="sm" lineHeight="1.6">
                          {study.challenge}
                        </Text>
                      </Box>

                      {/* Solution */}
                      <Box
                        bg="rgba(13, 127, 110, 0.06)"
                        p={5}
                        borderRadius="lg"
                        borderLeft="3px solid"
                        borderColor={PROPERTY_PRIMARY}
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="600"
                          letterSpacing="0.08em"
                          textTransform="uppercase"
                          color={PROPERTY_PRIMARY}
                          mb={2}
                        >
                          Our Solution
                        </Text>
                        <Text color="gray.700" fontSize="sm" lineHeight="1.6">
                          {study.solution}
                        </Text>
                      </Box>
                    </SimpleGrid>

                    {/* Results */}
                    <Box>
                      <Text
                        fontSize="xs"
                        fontWeight="700"
                        letterSpacing="0.08em"
                        textTransform="uppercase"
                        color="gray.500"
                        mb={3}
                      >
                        Key Results
                      </Text>
                      <VStack align="stretch" spacing={2}>
                        {study.results.map((result, idx) => (
                          <HStack key={idx} align="start">
                            <Icon as={FaCheckCircle} color={PROPERTY_PRIMARY} mt={0.5} />
                            <Text color="gray.700" fontSize="sm">
                              {result}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </VStack>
                </Box>

                {/* Metrics Sidebar */}
                <Box bg="gray.50" borderLeft={{ lg: "1px solid" }} borderColor="gray.100" p={6}>
                  <VStack spacing={0} align="stretch">
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      letterSpacing="0.08em"
                      textTransform="uppercase"
                      color="gray.500"
                      textAlign="center"
                      mb={4}
                    >
                      Key Metrics
                    </Text>
                    {study.metrics.map((metric, idx) => (
                      <Box
                        key={idx}
                        py={4}
                        borderBottom={idx < study.metrics.length - 1 ? "1px solid" : "none"}
                        borderColor="gray.200"
                        textAlign="center"
                      >
                        <Heading
                          size="lg"
                          color={metric.accent ? PROPERTY_PRIMARY : NAVY}
                        >
                          {metric.value}
                        </Heading>
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {metric.label}
                        </Text>
                      </Box>
                    ))}
                    <Button
                      mt={5}
                      bg={NAVY}
                      color="white"
                      _hover={{ bg: PROPERTY_PRIMARY }}
                      rightIcon={<FaArrowRight />}
                      onClick={() => navigate("/property/consultation")}
                    >
                      {study.ctaLabel}
                    </Button>
                  </VStack>
                </Box>
              </Grid>
            </Box>
          ))}
        </VStack>
      </Container>

      {/* Testimonial Section */}
      <Container maxW="container.lg" py={{ base: 12, md: 16 }}>
        <Box
          bg={NAVY}
          color="white"
          borderRadius="2xl"
          p={{ base: 8, md: 14 }}
          position="relative"
          overflow="hidden"
        >
          <Icon
            as={FaQuoteLeft}
            position="absolute"
            top="-10px"
            left="24px"
            boxSize={32}
            color={PROPERTY_PRIMARY}
            opacity={0.12}
            zIndex={0}
          />
          <SimpleGrid
            columns={{ base: 1, md: 2 }}
            spacing={10}
            alignItems="center"
            position="relative"
            zIndex={1}
          >
            <VStack align="start" spacing={5}>
              <Text fontSize={{ base: "lg", md: "xl" }} fontStyle="italic" lineHeight="1.7">
                "{TESTIMONIAL.quote}"
              </Text>
              <Box>
                <Text fontWeight="bold" color={PROPERTY_PRIMARY}>
                  {TESTIMONIAL.name}
                </Text>
                <Text fontSize="sm" color="whiteAlpha.700" mt={1}>
                  {TESTIMONIAL.description}
                </Text>
              </Box>
            </VStack>
            <HStack
              spacing={{ base: 8, md: 0 }}
              justify={{ base: "start", md: "flex-start" }}
              borderLeft={{ md: "1px solid" }}
              borderTop={{ base: "1px solid", md: "none" }}
              borderColor="whiteAlpha.200"
              pl={{ md: 10 }}
              pt={{ base: 6, md: 0 }}
              align="start"
            >
              <VStack align="start" spacing={6}>
                {TESTIMONIAL.stats.map((stat, idx) => (
                  <Box key={idx}>
                    <Heading size="xl" color={PROPERTY_PRIMARY}>
                      {stat.value}
                    </Heading>
                    <Text fontSize="sm" color="whiteAlpha.700">
                      {stat.label}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </HStack>
          </SimpleGrid>
        </Box>
      </Container>

      {/* CTA Section */}
      <Box bg="white" py={{ base: 16, md: 20 }} textAlign="center">
        <Container maxW="container.md">
          <Heading size="2xl" fontWeight="bold" color={NAVY} mb={4}>
            Ready to Secure Your Retirement?
          </Heading>
          <Text color="gray.600" fontSize="lg" lineHeight="1.7" maxW="480px" mx="auto" mb={8}>
            If you're earning well but concerned about retirement, you're not
            alone. Let's discuss how property investment can reduce your tax
            and build the wealth that super alone won't deliver.
          </Text>
          <HStack spacing={4} justify="center" flexWrap="wrap">
            <Button
              size="lg"
              bg={PROPERTY_PRIMARY}
              color="white"
              _hover={{ bg: "#0a6459" }}
              rightIcon={<FaArrowRight />}
              px={8}
              onClick={() => navigate("/property/consultation")}
            >
              Book Free Consultation
            </Button>
            <Button
              size="lg"
              variant="outline"
              borderColor={NAVY}
              color={NAVY}
              _hover={{ bg: NAVY, color: "white" }}
              px={8}
              onClick={() => navigate("/property/tools/investment-calculator")}
            >
              Try Our Calculator
            </Button>
          </HStack>
        </Container>
      </Box>

      {/* Disclaimer */}
      <Container maxW="container.lg" pb={10}>
        <Box pt={6} borderTop="1px solid" borderColor="gray.200">
          <Text fontSize="xs" color="gray.500" lineHeight="1.7">
            <Text as="span" fontWeight="bold">
              Important:
            </Text>{" "}
            These case studies are provided for illustrative purposes only and
            do not constitute financial, tax, or investment advice. Results
            are specific to individual circumstances and past performance is
            not indicative of future results. Property investment involves
            risk. You should seek independent financial and legal advice
            before making any investment decisions. One Group Australia's
            advisers are not licensed financial advisers under the
            Corporations Act 2001 (Cth). All client details have been
            de-identified in accordance with the Privacy Act 1988.
          </Text>
        </Box>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default PropertyCaseStudies;
